
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, StarIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';

interface EvaluationQuestion {
  id: string;
  text: string;
  averageRating: number;
  count: number;
}

interface ServiceEvaluation {
  serviceName: string;
  questions: EvaluationQuestion[];
}

interface ReviewEvaluationBreakdownProps {
  evaluations: ServiceEvaluation[];
}

const ReviewEvaluationBreakdown: React.FC<ReviewEvaluationBreakdownProps> = ({ 
  evaluations 
}) => {
  const [openServices, setOpenServices] = useState<Record<string, boolean>>({});

  const toggleService = (serviceName: string) => {
    setOpenServices(prev => ({
      ...prev,
      [serviceName]: !prev[serviceName]
    }));
  };

  const renderStars = (rating: number) => {
    return Array(5).fill(0).map((_, i) => (
      <StarIcon 
        key={i} 
        className={`w-4 h-4 ${i < Math.floor(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
      />
    ));
  };

  return (
    <Card>
      <Collapsible>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full flex justify-between items-center p-4 h-auto">
            <span className="font-medium text-lg">Detailed Evaluation Breakdown</span>
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          </Button>
        </CollapsibleTrigger>
        
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-4">
            {evaluations.map((service) => (
              <div key={service.serviceName}>
                <Button
                  variant="ghost"
                  onClick={() => toggleService(service.serviceName)}
                  className="flex items-center font-medium mb-2 p-0 h-auto w-full justify-start"
                >
                  {openServices[service.serviceName] ? 
                    <ChevronDown className="h-4 w-4 mr-1" /> : 
                    <ChevronRight className="h-4 w-4 mr-1" />
                  }
                  <span className="text-md">
                    {service.serviceName} Service
                  </span>
                </Button>
                
                {openServices[service.serviceName] && (
                  <div className="pl-6 space-y-4 mb-4">
                    {service.questions.map((question) => (
                      <div key={question.id} className="space-y-1">
                        <div className="text-sm flex justify-between">
                          <span>{question.text}</span>
                          <span className="font-medium">
                            {question.averageRating.toFixed(1)}/5
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex">
                            {renderStars(question.averageRating)}
                          </div>
                          <Progress 
                            value={(question.averageRating / 5) * 100} 
                            className="h-2 flex-1" 
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default ReviewEvaluationBreakdown;
